import React, { useState, useEffect, useCallback, useRef } from 'react';
import useAutoUpdate from '../hooks/useAutoUpdate';
import useWindowFocus from '../hooks/useWindowFocus';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Joyride, STATUS } from 'react-joyride';
import toast from 'react-hot-toast';
import HowToUse from '../components/Dashboard/HowToUse';
import VerifyPayment from './VerifyPayment';
import BulkEntry from '../components/Dashboard/BulkEntry';
import ManageCategories from '../components/Dashboard/ManageCategories';
import ManageMenu from '../components/Dashboard/ManageMenu';
import RestaurantSettings from '../components/Dashboard/RestaurantSettings';
import ManageCoupons from '../components/Dashboard/ManageCoupons';
import ManageOffers from '../components/Dashboard/ManageOffers';
import ManageNotifications from '../components/Dashboard/ManageNotifications';
import ManageQR from '../components/Dashboard/ManageQR';
import ManageSubscription from '../components/Dashboard/ManageSubscription';
import Analytics from '../components/Dashboard/Analytics';
import { enableAudio, speakAlert } from '../utils/speech';
import BillingSettings from '../components/Dashboard/BillingSettings';
import POS from '../components/Dashboard/POS';
import Announcements from '../components/Dashboard/Announcements';
import Gallery from '../components/Dashboard/Gallery';
import RecentOrdersWidget from '../components/Dashboard/RecentOrdersWidget';
import KitchenView from '../components/Dashboard/KitchenView';
import StaffManagement from '../components/Dashboard/StaffManagement';
import ManageServiceRequests from '../components/Dashboard/ManageServiceRequests';
import AdminMonetization from './AdminMonetization';
import LoyaltyProgram from '../components/Dashboard/LoyaltyProgram';
import Footer from '../components/Footer';
import usePWAInstall from '../hooks/usePWAInstall';
import '../styles/admin.css';

const BASE = 'https://menu.searchisend.in';

// ─── Upgrade Modal ──────────────────────────────────────────────────────────
const UpgradeModal = ({ feature, onClose }) => {
    const benefits = {
        'Special Offers':     ['Show promotional popup images when customers open your menu', 'Upload up to 3 rotating offer banners', 'Drive more daily specials & combo awareness'],
        'Coupon System':      ['Create discount codes for percentage or flat-off deals', 'Set expiry dates and usage limits', 'Share codes on WhatsApp, Instagram or flyers'],
        'Push Notifications': ['Send instant messages to all subscribers', 'Announce offers, events or daily specials for free', 'Subscribers opt-in by scanning your QR — no sign-up needed'],
    };
    const list = benefits[feature] || [];
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={onClose}>
            <div style={{ background: 'var(--card-bg-color, #fff)', borderRadius: '16px', padding: '32px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{ width: '56px', height: '56px', background: 'linear-gradient(135deg, #fef3c7, #fde68a)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                        <i className="fas fa-crown" style={{ fontSize: '1.6rem', color: '#f59e0b' }} />
                    </div>
                    <h2 style={{ margin: '0 0 6px 0', fontSize: '1.3rem' }}>Unlock {feature}</h2>
                    <p style={{ margin: 0, color: 'var(--text-color-secondary)', fontSize: '0.9em' }}>This feature is available on the Paid Plan.</p>
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0' }}>
                    {list.map((b, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px', fontSize: '0.93em', color: 'var(--text-color)' }}>
                            <i className="fas fa-check-circle" style={{ color: '#10b981', marginTop: '2px', flexShrink: 0 }} />
                            {b}
                        </li>
                    ))}
                </ul>
                <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                    <Link to="/manage_v2_secure_auth/subscription" onClick={onClose} style={{ display: 'block', textAlign: 'center', padding: '12px', background: 'var(--primary-orange, #ea580c)', color: '#fff', borderRadius: '50px', fontWeight: '700', textDecoration: 'none', boxShadow: '0 4px 14px rgba(234,88,12,0.3)' }}>
                        <i className="fas fa-crown" style={{ marginRight: '8px' }} />View Subscription Plans
                    </Link>
                    <button onClick={onClose} style={{ border: '1px solid var(--border-color)', background: 'none', padding: '10px', borderRadius: '50px', cursor: 'pointer', fontWeight: '600', color: 'var(--text-color-secondary)', fontSize: '0.9em' }}>Maybe Later</button>
                </div>
            </div>
        </div>
    );
};

// ─── Scanner Prompt Modal ───────────────────────────────────────────────────
const ScannerPromptModal = ({ onClose, onScanSuccess, token, dashData, user }) => {
    const aiFileRef = React.useRef(null);
    const [isScanning, setIsScanning] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [scanMessage, setScanMessage] = useState('');

    const handleAIPhotoSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const aiCredits = dashData?.stats?.ai_credits || 0;
        if (aiCredits <= 0) {
            alert("No AI Scans remaining. Please contact support or purchase more credits.");
            return;
        }

        setIsScanning(true);
        setScanMessage('Uploading image for analysis...');

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = async () => {
            const base64data = reader.result;
            setScanMessage('Analyzing menu layout and extracting dishes...');
            try {
                const res = await fetch(`${BASE}/apimenu/v2/v2_ai_parser.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ image: base64data })
                });
                const data = await res.json();
                if (data.success) {
                    setIsSuccess(true);
                } else {
                    alert(data.message || 'Analysis failed.');
                    setIsScanning(false);
                }
            } catch (err) {
                alert('Network Error during scan.');
                setIsScanning(false);
            } finally {
                if(aiFileRef.current) aiFileRef.current.value = '';
            }
        };
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(4px)' }}>
            <div style={{ background: 'var(--card-bg-color, #fff)', borderRadius: '24px', padding: '40px 32px', maxWidth: '420px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', textAlign: 'center' }}>
                
                {isSuccess ? (
                    <>
                        <div style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                            <i className="fas fa-check-circle" style={{ fontSize: '2.5rem', color: '#16a34a' }} />
                        </div>
                        <h2 style={{ margin: '0 0 12px 0', fontSize: '1.6rem', fontWeight: '800' }}>Congratulations!</h2>
                        <p style={{ margin: '0 0 24px 0', color: 'var(--text-color-secondary)', fontSize: '1rem', lineHeight: '1.5' }}>
                            Your menu has been successfully extracted and created using AI.
                        </p>
                        <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                <p style={{ margin: '0 0 5px 0', fontSize: '0.9em', color: '#64748b', fontWeight: 600 }}>Your Live Menu Link:</p>
                                <a href={`https://menu.searchisend.in/${dashData?.settings?.menu_slug || ''}`} target="_blank" rel="noopener noreferrer" style={{ display: 'block', wordBreak: 'break-all', color: 'var(--primary-orange)', fontWeight: '700', textDecoration: 'none', fontSize: '1.05em' }}>
                                    menu.searchisend.in/{dashData?.settings?.menu_slug || ''}
                                </a>
                            </div>
                            <button 
                                onClick={() => {
                                    navigator.clipboard.writeText(`https://menu.searchisend.in/${dashData?.settings?.menu_slug || ''}`);
                                    toast.success('Link copied!');
                                }}
                                style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '8px', borderRadius: '8px', transition: 'background 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onMouseOver={e => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = 'var(--primary-orange)'; }}
                                onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
                                title="Copy Link"
                            >
                                <i className="fas fa-copy" style={{ fontSize: '1.2rem' }}></i>
                            </button>
                        </div>
                        <button onClick={() => {
                            onScanSuccess();
                            window.location.reload();
                        }} style={{ display: 'block', width: '100%', padding: '14px', background: 'var(--primary-orange, #ea580c)', color: '#fff', borderRadius: '50px', fontWeight: '700', fontSize: '1.05rem', border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(234,88,12,0.3)', transition: 'transform 0.2s' }} onMouseOver={e => e.currentTarget.style.transform = 'scale(1.02)'} onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}>
                            View & Edit Dashboard
                        </button>
                    </>
                ) : isScanning ? (
                    <div style={{ padding: '20px 0' }}>
                        <div style={{ width: '60px', height: '60px', border: '4px solid #f3f4f6', borderTop: '4px solid #ea580c', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' }} />
                        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                        <h2 style={{ margin: '0 0 10px 0', fontSize: '1.4rem' }}>{scanMessage}</h2>
                        <p style={{ color: 'var(--text-color-secondary)', fontSize: '0.9em' }}>This usually takes about 10-20 seconds. Please don't close this window.</p>
                    </div>
                ) : (
                    <>
                        <div style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #ffedd5, #fed7aa)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                            <i className="fas fa-magic" style={{ fontSize: '2.5rem', color: '#ea580c' }} />
                        </div>
                        <h2 style={{ margin: '0 0 12px 0', fontSize: '1.6rem', fontWeight: '800' }}>Create Menu Instantly!</h2>
                        <p style={{ margin: '0 0 20px 0', color: 'var(--text-color-secondary)', fontSize: '0.95em', lineHeight: '1.5' }}>
                            Don't type everything by hand. Just upload a photo of your existing paper menu, and our smart AI Scanner will automatically extract your dishes and prices.
                        </p>
                        <div style={{ background: 'rgba(234, 88, 12, 0.1)', color: '#ea580c', padding: '12px', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '24px', border: '1px solid rgba(234, 88, 12, 0.2)', display: 'flex', alignItems: 'flex-start', textAlign: 'left', gap: '8px' }}>
                            <i className="fas fa-info-circle" style={{ marginTop: '2px' }} /> 
                            <span><strong>Note:</strong> Since this uses AI, it might make small mistakes. You can easily review and edit the extracted items after scanning!</span>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                            <input type="file" accept="image/*" ref={aiFileRef} style={{ display: 'none' }} onChange={handleAIPhotoSelect} />
                            
                            <button onClick={() => aiFileRef.current.click()} style={{ display: 'block', width: '100%', padding: '14px', background: 'var(--primary-orange, #ea580c)', color: '#fff', borderRadius: '50px', fontWeight: '700', fontSize: '1.05rem', border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(234,88,12,0.3)', transition: 'transform 0.2s' }} onMouseOver={e => e.currentTarget.style.transform = 'scale(1.02)'} onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}>
                                <i className="fas fa-camera" style={{ marginRight: '8px' }} /> Upload Menu Photo
                            </button>
                            <button onClick={onClose} style={{ border: 'none', background: 'none', padding: '12px', cursor: 'pointer', fontWeight: '600', color: 'var(--text-color-secondary)', fontSize: '0.95em', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = 'var(--text-color)'} onMouseOut={e => e.currentTarget.style.color = 'var(--text-color-secondary)'}>
                                I'll add items manually
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// ─── Custom Tour Tooltip ────────────────────────────────────────────────────
const CustomTooltip = ({ index, step, backProps, closeProps, primaryProps, skipProps, tooltipProps, isLastStep }) => {
    
    // Fallback: If Joyride's internal callback fails to fire for any reason,
    // this aggressively forces the completion flow directly from the button click.
    const forceComplete = () => {
        localStorage.setItem('tour_completed_backup', 'true');
        setTimeout(() => window.location.reload(), 150); // Small delay to let joyride advance visually before reload
    };

    return (
        <div {...tooltipProps} style={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0px 14px 28px rgba(0,0,0,0.12), 0px 10px 10px rgba(0,0,0,0.08)', padding: '24px', maxWidth: '380px', boxSizing: 'border-box', fontFamily: "'Outfit', sans-serif", display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {step.title && (
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {step.title}
                </h3>
            )}
            <div style={{ fontSize: '1rem', color: '#4b5563', lineHeight: '1.5' }}>
                {step.content}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                <div>
                    {index > 0 && (
                        <button {...backProps} style={{ backgroundColor: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', fontWeight: '600', padding: '8px', fontSize: '0.9em' }}>
                            Back
                        </button>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                    <button 
                        {...skipProps} 
                        onClick={(e) => {
                            if (skipProps.onClick) skipProps.onClick(e);
                            forceComplete();
                        }}
                        style={{ backgroundColor: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', fontWeight: '500', padding: '8px', fontSize: '0.9em' }}
                    >
                        Skip
                    </button>
                    <button 
                        {...primaryProps} 
                        onClick={(e) => {
                            if (primaryProps.onClick) primaryProps.onClick(e);
                            if (isLastStep) {
                                forceComplete();
                            }
                        }}
                        style={{ backgroundColor: 'var(--primary-orange, #ea580c)', border: 'none', color: '#fff', padding: '10px 22px', borderRadius: '50px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.95em', boxShadow: '0 4px 12px rgba(234, 88, 12, 0.3)', transition: 'transform 0.2s' }} 
                        onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'} 
                        onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                        {isLastStep ? 'Finish' : 'Next'} {!isLastStep && <i className="fas fa-arrow-right" style={{ fontSize: '0.85em' }}/>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Subscription Timer Badge ──────────────────────────────────────────────
const SubscriptionBadge = ({ timerClass, timerText }) => {
    const colors = {
        'timer-safe':    { bg: '#d4edda', color: '#155724', icon: 'fa-circle-check' },
        'timer-warning': { bg: '#fff3cd', color: '#856404', icon: 'fa-clock'        },
        'timer-danger':  { bg: '#f8d7da', color: '#721c24', icon: 'fa-clock'        },
        'timer-expired': { bg: '#f8d7da', color: '#721c24', icon: 'fa-times-circle' },
    };
    const s = colors[timerClass] || colors['timer-safe'];
    return (
        <div style={{ padding: '7px 14px', borderRadius: '50px', fontWeight: '600', fontSize: '0.88em', backgroundColor: s.bg, color: s.color, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <i className={`fas ${s.icon}`} style={{ fontSize: '0.9em' }} />
            {timerText}
        </div>
    );
};

// ─── Dashboard Overview ─────────────────────────────────────────────────────
const DashboardOverview = ({ user, dashData, token, setDash, onRestartTour, onUpgradeClick }) => {
    const stats = dashData?.stats;
    const feat  = dashData?.features;
    const ann   = dashData?.announcement;

    const navLinks = [
        { to: 'manage-menu',          icon: 'fa-utensils',      label: 'Manage My Menu',            always: true  },
        { to: 'manage-categories',    icon: 'fa-list-alt',      label: 'Manage Categories',         always: true  },
        { to: 'qr-templates',         icon: 'fa-qrcode',        label: 'Download QR Templates',     always: true  },
        { to: 'analytics',            icon: 'fa-chart-line',    label: 'Detailed Analytics',        always: true  },
        { to: 'manage-offers',        icon: 'fa-tags',          label: 'Manage Special Offers',     feature: 'has_offer_popup',       featureLabel: 'Special Offers'   },
        { to: 'manage-coupons',       icon: 'fa-ticket-alt',    label: 'Manage Coupons',            feature: 'has_coupon_system',     featureLabel: 'Coupon System'    },
        { to: 'manage-notifications', icon: 'fa-bell',          label: 'Send Notifications',        feature: 'has_push_notification', featureLabel: 'Push Notifications' },
        { to: 'bulk-entry',           icon: 'fa-bolt',          label: 'Rapid Bulk Menu Entry',     always: true  },
        { to: 'pos',                  icon: 'fa-cash-register', label: 'Orders & POS',             always: true  },
        { to: 'kitchen',              icon: 'fa-fire-burner',   label: 'Kitchen Display (KOT)',    always: true  },
        { to: 'service-requests',     icon: 'fa-bell',          label: 'Service Requests',          always: true  },
        { to: 'staff',                icon: 'fa-users-cog',     label: 'Staff Management',          feature: 'has_staff_management', featureLabel: 'Staff Management' },
        { to: 'billing-settings',     icon: 'fa-file-invoice-dollar', label: 'Billing Settings',     always: true  },
        { to: 'settings',             icon: 'fa-cog',           label: 'Restaurant Settings',       always: true  },
        { to: 'subscription',         icon: 'fa-receipt',       label: 'My Subscription & Renewal', always: true  },
        { to: 'monetization',         icon: 'fa-dollar-sign',   label: 'Ad Monetization',           feature: 'has_ad_monetization', featureLabel: 'Ad Monetization' },
        { to: 'loyalty',              icon: 'fa-gift',          label: 'Loyalty Program',           feature: 'has_loyalty_system', featureLabel: 'Loyalty Program' },
        { to: 'how-to-use',           icon: 'fa-book-open',     label: 'How to Use',                always: true  },
    ];

    const hasBrandAndContact = !!dashData?.settings?.theme_color && !!dashData?.settings?.phone_for_orders;
    const hasDownloadedQR = localStorage.getItem('qr_downloaded') === 'true';

    let frontendScore = 0;
    if (stats?.total_items > 0) frontendScore += 25;
    if (dashData?.settings?.logo_url) frontendScore += 25;
    if (hasBrandAndContact) frontendScore += 25;
    if (hasDownloadedQR) frontendScore += 25;

    const isProfileIncomplete = frontendScore < 100;

    return (
        <div className="admin-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h1 style={{ fontSize: '2rem', fontWeight: '700', margin: 0 }}>Welcome!</h1>
                <button onClick={onRestartTour} style={{ backgroundColor: 'transparent', color: 'var(--text-color)', border: '1px solid var(--border-color)', padding: '6px 12px', borderRadius: '50px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85em' }}>
                    <i className="fas fa-play-circle" style={{ color: 'var(--primary-orange)' }} /> Restart Demo
                </button>
            </div>
            <p style={{ color: 'var(--text-color-secondary)', marginBottom: '24px' }}>
                This is your dashboard. From here, you can manage your menu and view your subscription details.
            </p>

            {/* Live Menu Link */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', padding: '16px 20px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '28px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <div>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '1.05em', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fas fa-external-link-alt" style={{ color: 'var(--primary-orange)' }}></i> Your Live Menu Link
                    </h3>
                    <a href={`https://menu.searchisend.in/${dashData?.settings?.menu_slug || ''}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-orange)', fontWeight: '600', textDecoration: 'none', fontSize: '0.95em', wordBreak: 'break-all' }}>
                        https://menu.searchisend.in/{dashData?.settings?.menu_slug || ''}
                    </a>
                </div>
                <button 
                    onClick={() => {
                        const link = `https://menu.searchisend.in/${dashData?.settings?.menu_slug || ''}`;
                        if (navigator.clipboard && window.isSecureContext) {
                            navigator.clipboard.writeText(link)
                                .then(() => alert('Live menu link copied to clipboard!'))
                                .catch(() => alert('Failed to copy link.'));
                        } else {
                            const textArea = document.createElement("textarea");
                            textArea.value = link;
                            textArea.style.position = "fixed";
                            textArea.style.left = "-999999px";
                            document.body.appendChild(textArea);
                            textArea.focus();
                            textArea.select();
                            try {
                                document.execCommand('copy');
                                alert('Live menu link copied to clipboard!');
                            } catch (error) {
                                alert('Failed to copy link.');
                            } finally {
                                textArea.remove();
                            }
                        }
                    }} 
                    style={{ background: '#f8fafc', border: '1px solid #cbd5e1', color: '#475569', padding: '8px 16px', borderRadius: '50px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85em', transition: 'all 0.2s' }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--primary-orange)'; e.currentTarget.style.color = 'var(--primary-orange)'; }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#475569'; }}
                >
                    <i className="far fa-copy"></i> Copy Link
                </button>
            </div>

            {/* Recent Orders Widget */}
            <RecentOrdersWidget token={token} />

            {/* Announcement Box */}
            {ann && (
                <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', color: '#4338ca', padding: '18px 20px', marginBottom: '28px', borderRadius: 'var(--border-radius-md)' }}>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '1em' }}><i className="fas fa-bullhorn" style={{ marginRight: '8px' }} />{ann.title}</h3>
                    <p style={{ margin: 0, fontSize: '0.95em', whiteSpace: 'pre-line' }}>{ann.message}</p>
                    {ann.button_text && ann.button_url && (
                        <a href={ann.button_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: '12px', padding: '8px 18px', backgroundColor: '#4338ca', color: '#fff', borderRadius: '50px', fontWeight: '600', textDecoration: 'none', fontSize: '0.9em' }}>{ann.button_text}</a>
                    )}
                    <p style={{ margin: '12px 0 0 0' }}><Link to="announcements" style={{ fontWeight: '600', color: '#4338ca', fontSize: '0.9em' }}>View All Announcements →</Link></p>
                </div>
            )}

            {/* Gamified Setup Checklist Widget */}
            {isProfileIncomplete && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '28px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <div>
                            <h3 style={{ margin: '0 0 4px 0', fontSize: '1.15em', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <i className="fas fa-rocket" style={{ color: '#f59e0b' }}></i> Getting Started Checklist
                            </h3>
                            <p style={{ margin: 0, fontSize: '0.9em', color: '#6b7280' }}>Complete these steps to make your menu live!</p>
                        </div>
                        <div style={{ fontWeight: '700', color: '#ea580c', fontSize: '1.2em' }}>
                            {frontendScore}%
                        </div>
                    </div>
                    
                    <div style={{ width: '100%', height: '8px', background: '#f3f4f6', borderRadius: '10px', overflow: 'hidden', marginBottom: '20px' }}>
                        <div style={{ width: `${frontendScore}%`, height: '100%', background: 'linear-gradient(90deg, #f59e0b, #ea580c)', transition: 'width 0.5s ease' }}></div>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Task 1: Menu Items */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: stats?.total_items > 0 ? '#10b981' : '#f3f4f6', color: stats?.total_items > 0 ? '#fff' : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8em' }}>
                                <i className={stats?.total_items > 0 ? "fas fa-check" : "fas fa-circle"} />
                            </div>
                            <span style={{ fontSize: '0.95em', color: stats?.total_items > 0 ? '#6b7280' : '#374151', textDecoration: stats?.total_items > 0 ? 'line-through' : 'none' }}>Add your first menu item</span>
                            {stats?.total_items === 0 && <Link to="manage-menu" style={{ marginLeft: 'auto', fontSize: '0.85em', color: '#ea580c', fontWeight: '600', textDecoration: 'none' }}>Go &rarr;</Link>}
                        </div>
                        
                        {/* Task 2: Logo */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: dashData?.settings?.logo_url ? '#10b981' : '#f3f4f6', color: dashData?.settings?.logo_url ? '#fff' : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8em' }}>
                                <i className={dashData?.settings?.logo_url ? "fas fa-check" : "fas fa-circle"} />
                            </div>
                            <span style={{ fontSize: '0.95em', color: dashData?.settings?.logo_url ? '#6b7280' : '#374151', textDecoration: dashData?.settings?.logo_url ? 'line-through' : 'none' }}>Upload your restaurant logo</span>
                            {!dashData?.settings?.logo_url && <Link to="settings" style={{ marginLeft: 'auto', fontSize: '0.85em', color: '#ea580c', fontWeight: '600', textDecoration: 'none' }}>Go &rarr;</Link>}
                        </div>

                        {/* Task 3: Theme Color */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: hasBrandAndContact ? '#10b981' : '#f3f4f6', color: hasBrandAndContact ? '#fff' : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8em' }}>
                                <i className={hasBrandAndContact ? "fas fa-check" : "fas fa-circle"} />
                            </div>
                            <span style={{ fontSize: '0.95em', color: hasBrandAndContact ? '#6b7280' : '#374151', textDecoration: hasBrandAndContact ? 'line-through' : 'none' }}>Set your brand colors & contact</span>
                            {!hasBrandAndContact && <Link to="settings" style={{ marginLeft: 'auto', fontSize: '0.85em', color: '#ea580c', fontWeight: '600', textDecoration: 'none' }}>Go &rarr;</Link>}
                        </div>

                        {/* Task 4: Download QR */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: hasDownloadedQR ? '#10b981' : '#f3f4f6', color: hasDownloadedQR ? '#fff' : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8em' }}>
                                <i className={hasDownloadedQR ? "fas fa-check" : "fas fa-circle"} />
                            </div>
                            <span style={{ fontSize: '0.95em', color: hasDownloadedQR ? '#6b7280' : '#374151', textDecoration: hasDownloadedQR ? 'line-through' : 'none' }}>Download your QR Code</span>
                            {!hasDownloadedQR && <Link to="qr-templates" style={{ marginLeft: 'auto', fontSize: '0.85em', color: '#ea580c', fontWeight: '600', textDecoration: 'none' }}>Go &rarr;</Link>}
                        </div>
                    </div>
                </div>
            )}

            {/* Stats — responsive grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: '28px' }}>
                {[
                    { label: 'Total Menu Items',         value: stats?.total_items        ?? '…' },
                    { label: 'Total Categories',         value: stats?.total_categories   ?? '…' },
                    { label: 'Notification Subscribers', value: stats?.total_subscribers  ?? '…' },
                ].map(s => (
                    <div key={s.label} className="stat-card-premium">
                        <p style={{ margin: '0 0 12px 0', color: 'var(--text-color-secondary)', fontSize: '0.95em', fontWeight: '500' }}>{s.label}</p>
                        <p style={{ fontSize: '3rem', fontWeight: '800', color: 'var(--primary-orange)', margin: 0, lineHeight: 1 }}>{s.value}</p>
                    </div>
                ))}
                
                {/* Growth Metric: Total Views */}
                <div className="stat-card-premium" style={{ background: 'linear-gradient(to bottom right, #fff, #fff9f5)' }}>
                    <p style={{ margin: '0 0 12px 0', color: 'var(--text-color-secondary)', fontSize: '0.95em', fontWeight: '500' }}>
                        <i className="fas fa-eye" style={{ marginRight: '6px', color: '#9ca3af' }} /> Total Menu Views
                    </p>
                    <p style={{ fontSize: '3rem', fontWeight: '800', color: 'var(--primary-orange)', margin: 0, lineHeight: 1 }}>
                        {dashData?.settings?.total_views || 0}
                    </p>
                </div>
            </div>

            {/* Nav Links — plain uniform style, locked features visible */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {navLinks.map(link => {
                    const isLocked = !link.always && link.feature && !feat?.[link.feature];
                    if (isLocked) {
                        return (
                            <button
                                key={link.to}
                                id={`nav-${link.to}`}
                                onClick={() => onUpgradeClick && onUpgradeClick(link.featureLabel)}
                                className="dashboard-nav-link"
                                style={{ opacity: 0.65, cursor: 'pointer', textAlign: 'left', border: 'none', width: '100%', display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}
                            >
                                <i className={`fas ${link.icon}`} />
                                {link.label}
                                <span style={{ marginLeft: 'auto', fontSize: '0.78em', background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', padding: '2px 10px', borderRadius: '50px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <i className="fas fa-lock" style={{ fontSize: '0.8em' }} /> Upgrade
                                </span>
                            </button>
                        );
                    }
                    return (
                        <Link
                            key={link.to}
                            id={`nav-${link.to}`}
                            to={link.to}
                            className="dashboard-nav-link"
                        >
                            <i className={`fas ${link.icon}`} />
                            {link.label}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};


// ─── Back Link Wrapper ──────────────────────────────────────────────────────
const withBack = (Component, label = 'Back to Dashboard') =>
    (props) => (
        <div className="admin-card">
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
                <i className="fas fa-arrow-left"></i> {label}
            </Link>
            <Component {...props} />
        </div>
    );

const BulkEntryWrapped = withBack(BulkEntry);

// ─── Main AdminDashboard ────────────────────────────────────────────────────

const AdminDashboard = () => {
    useAutoUpdate('admin');
    const location = useLocation();
    const [token, setToken]         = useState(null);
    const [user, setUser]           = useState(null);
    const [dashData, setDash]       = useState(null);
    const [runTour, setRunTour]     = useState(false);
    const [tourKey, setTourKey]     = useState(0);
    const [upgradeModal, setUpgradeModal] = useState(null); // null | feature label string
    const [showScannerPrompt, setShowScannerPrompt] = useState(false);
    const [audioUnlocked, setAudioUnlocked] = useState(false);
    usePWAInstall(); // Initialize PWA install hook
    const prevServiceRequestIds = useRef(new Set());
    const navigate                  = useNavigate();

    const playBeep = () => {
        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            const ctx = new AudioContext();
            const osc = ctx.createOscillator();
            const gainNode = ctx.createGain();
            osc.connect(gainNode);
            gainNode.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.setValueAtTime(880, ctx.currentTime); // A5 note
            gainNode.gain.setValueAtTime(1, ctx.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
            osc.start();
            osc.stop(ctx.currentTime + 0.5);
        } catch(e) {
            console.log('Audio synthesis failed', e);
        }
    };

    const saveTourCompleted = async () => {
        localStorage.setItem('tour_completed_backup', 'true');
        setDash(prev => ({
            ...prev,
            settings: { ...prev?.settings, tour_completed: true }
        }));
        const authToken = localStorage.getItem('saas_token');
        if (authToken) {
            try {
                const tourRes = await fetch(`${BASE}/apimenu/v2/v2_dashboard.php?action=complete_tour`, {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${authToken}` }
                });
                const tourData = await tourRes.json();
                if (!tourData.success) {
                    console.warn('[Tour] DB save failed:', tourData.message);
                }
            } catch (fetchErr) {
                console.warn('[Tour] Network error saving tour completion:', fetchErr);
            }
        }
    };

    useEffect(() => {
        if (dashData && dashData.settings) {
            const isLocalDone = localStorage.getItem('tour_completed_backup') === 'true';
            const willShowScanner = dashData.stats && dashData.stats.total_items === 0 && !sessionStorage.getItem('ai_prompt_done') && location.pathname === '/manage_v2_secure_auth/';

            if (dashData.settings.tour_completed === false && !isLocalDone && !willShowScanner) {
                // Mark as done IMMEDIATELY (before user clicks Skip/Finish)
                // so browser-Refresh doesn't re-trigger the tour
                saveTourCompleted();
                setRunTour(true);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashData]);

    const handleJoyrideCallback = async (data) => {
        try {
            console.log('🔥 JOYRIDE FULL PAYLOAD:', data); // Detailed debug log for every step
            
            const { status, action, type } = data;
            
            const isFinished = ['finished', 'skipped', 'error'].includes(status) || 
                               action === 'close' || 
                               type === 'tour:end' || 
                               type === 'error:target_not_found' ||
                               (typeof STATUS !== 'undefined' && (status === STATUS.FINISHED || status === STATUS.SKIPPED));

            if (isFinished) {
                setRunTour(false);
                localStorage.setItem('tour_completed_backup', 'true');
                if (setDash) {
                    setDash(prev => ({
                        ...prev,
                        settings: { ...prev?.settings, tour_completed: true }
                    }));
                }
                const authToken = localStorage.getItem('saas_token');
                if (authToken) {
                    try {
                        const tourRes = await fetch(`${BASE}/apimenu/v2/v2_dashboard.php?action=complete_tour`, {
                            method: 'GET',
                            headers: { 'Authorization': `Bearer ${authToken}` }
                        });
                        const tourData = await tourRes.json();
                        if (!tourData.success) {
                            console.warn('[Tour] DB save failed:', tourData.message);
                        }
                    } catch (fetchErr) {
                        console.warn('[Tour] Network error saving tour completion:', fetchErr);
                    }
                }
                
                // Refresh the page to restore all UI elements cleanly
                window.location.reload();
            }
        } catch (e) {
            console.error('Joyride Parent Callback Error:', e);
        }
    };

    const handleRestartTour = () => {
        localStorage.removeItem('tour_completed_backup');
        setTourKey(prev => prev + 1);
        setRunTour(true);
    };

    const joyrideSteps = [
        { target: 'body', title: '👋 Welcome to Dashboard!', content: 'Let us give you a quick tour to get started building your digital menu.', placement: 'center' },
        { target: '#nav-manage-categories', title: 'Step 1: Categories', content: 'First, create your food categories (like Starters or Mains) here.', placement: 'right' },
        { target: '#nav-manage-menu', title: 'Step 2: Menu Items', content: 'Add your food items into those categories here!', placement: 'right' },
        { target: '#nav-qr-templates', title: 'Step 3: Print QR Code', content: 'Download and print your QR code to put on tables.', placement: 'right' },
        { target: '#nav-pos', title: 'Enterprise POS', content: 'Process in-house orders, manage table billing, and create instant receipts.', placement: 'right' },
        { target: '#nav-analytics', title: 'Analytics & Traffic', content: 'Track menu scans, popular items, and total sales volume.', placement: 'right' },
        { target: '#nav-settings', title: 'Settings & Branding', content: 'Set your restaurant logo and theme colors to match your brand.', placement: 'right' },
        { target: '#nav-billing-settings', title: 'Billing Configuration', content: 'Configure taxes (CGST/SGST) and define your receipt headers.', placement: 'right' }
    ];

    useEffect(() => {
        const storedToken = localStorage.getItem('saas_token');
        const storedUser  = localStorage.getItem('saas_user');
        if (storedToken && storedUser) {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
        } else {
            navigate('/login');
        }
    }, [navigate]);

    const unlockAudio = () => {
        enableAudio();
        setAudioUnlocked(true);
    };

    const fetchAlerts = useCallback(async () => {
        if (!token) return;
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_alerts.php?action=fetch&target_role=admin`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const json = await res.json();
            if (json.success && json.alerts && json.alerts.length > 0) {
                json.alerts.forEach(async (alert) => {
                    speakAlert(alert.message_text);
                    await fetch(`${BASE}/apimenu/v2/v2_alerts.php?action=resolve`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ alert_id: alert.id })
                    });
                });
            }
        } catch(err) {
            console.error("Alerts fetch error:", err);
        }
    }, [token]);

    const fetchDash = useCallback(async (t, silent = false) => {
        try {
            const res  = await fetch(`${BASE}/apimenu/v2/v2_dashboard.php?action=fetch`, { headers: { Authorization: `Bearer ${t}` } });
            if (res.status === 401) {
                localStorage.removeItem('saas_token');
                localStorage.removeItem('saas_user');
                navigate('/login');
                return;
            }
            const data = await res.json();
            if (data.success) {
                setDash(data);
                
                // NEW: Force redirect if expired
                if (data.subscription?.status === 'Expired' && location.pathname !== '/manage_v2_secure_auth/subscription') {
                    navigate('/manage_v2_secure_auth/subscription');
                    return;
                }
                
                // NEW: Show Scanner Prompt for new users to use AI Scanner
                if (data.stats && data.stats.total_items === 0 && !sessionStorage.getItem('ai_prompt_done') && location.pathname === '/manage_v2_secure_auth/') {
                    sessionStorage.setItem('ai_prompt_done', 'true');
                    setShowScannerPrompt(true);
                }
            } else if (data.message && (data.message.includes('Token') || data.message.includes('Authorization'))) {
                localStorage.removeItem('saas_token');
                localStorage.removeItem('saas_user');
                navigate('/login');
            }
        } catch (e) {
            console.error('Dashboard fetch error:', e);
            if (e.name === 'TypeError' && e.message.includes('fetch')) {
                console.warn('Network offline or connecting. Retrying in 5 seconds...');
                setTimeout(() => fetchDash(t, true), 5000);
            }
        }
    }, [navigate, location.pathname]);

    useEffect(() => {
        if (token) {
            fetchDash(token);
            fetchAlerts();
        }
    }, [token, fetchDash, fetchAlerts]);

    // Re-fetch instantly when user brings tab to focus
    useWindowFocus(() => {
        if (token) {
            fetchDash(token, true);
        }
    });

    useEffect(() => {
        let interval;
        if (user?.id) {
            const fetchServiceRequests = async () => {
                try {
                    const res = await fetch(`${BASE}/apimenu/get_service_requests.php?customer_id=${user.id}`);
                    const data = await res.json();
                    if (data.status === 'success') {
                        const newRequests = data.data;
                        
                        // Check if there is a NEW request that we haven't seen yet
                        const hasNew = newRequests.some(r => !prevServiceRequestIds.current.has(r.id));
                        
                        if (hasNew) {
                            playBeep();
                            if (navigator.vibrate) {
                                navigator.vibrate([200, 100, 200, 100, 500]);
                            }
                        }
                        
                        // Update ref with current requests
                        prevServiceRequestIds.current = new Set(newRequests.map(r => r.id));
                    }
                } catch (e) {
                    console.error('Service polling error:', e);
                }
            };
            
            fetchServiceRequests();
            interval = setInterval(() => {
                fetchServiceRequests();
                fetchDash(token, true);
                fetchAlerts(); 
            }, 10000); // 10s poll
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [user?.id, token, fetchDash, fetchAlerts]);
    
    const handleLogout = () => {
        localStorage.removeItem('saas_token');
        localStorage.removeItem('saas_user');
        navigate('/login');
    };

    const headerRef = React.useRef(null);
    
    useEffect(() => {
        if (!headerRef.current) return;
        const resizeObserver = new ResizeObserver(() => {
            if (headerRef.current) {
                document.documentElement.style.setProperty('--header-height', `${headerRef.current.offsetHeight}px`);
            }
        });
        resizeObserver.observe(headerRef.current);
        return () => resizeObserver.disconnect();
    }, [token, dashData]); // Re-run if token/dashData changes as it might render different header content

    if (!token) return <div style={{ padding: '50px', textAlign: 'center' }}>Loading Secure Environment…</div>;

    const sub  = dashData?.subscription;
    const hasUnread = dashData?.has_unread_announcement;

    return (
        <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-color)', color: 'var(--text-color)' }}>

            {/* ── Header ── */}
            <header ref={headerRef} className="app-header" style={{ position: 'sticky', top: 0, zIndex: 100, borderRadius: 0, display: runTour ? 'none' : 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'clamp(12px, 2vw, 14px) clamp(12px, 3vw, 40px)', background: 'var(--card-bg-color)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderBottom: '1px solid var(--border-color)', flexWrap: 'wrap', gap: '10px' }}>
                <div className="logo-area" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <img
                        src={dashData?.settings?.logo_url || `${BASE}/logo512.png`}
                        alt="Logo"
                        style={{ width: '46px', height: '46px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border-color)' }}
                        onError={e => { e.target.src = `${BASE}/logo512.png`; }}
                    />
                    <div>
                        <h1 className="title" style={{ margin: 0, fontSize: '1.4rem', fontWeight: '700', lineHeight: 1.2 }}>{user?.restaurant_name || 'Admin'}</h1>
                        <p className="subtitle" style={{ margin: 0, color: 'var(--text-color-secondary)', fontSize: '0.85rem' }}>Admin Panel</p>
                    </div>
                </div>

                <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    {!audioUnlocked && (
                        <button 
                            onClick={unlockAudio}
                            style={{ background: '#fef3c7', color: '#d97706', border: '1px solid #fde68a', padding: '8px 16px', borderRadius: '50px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                            <i className="fas fa-volume-up" /> Enable Audio Alerts
                        </button>
                    )}
                    {/* Announcements with optional unread dot */}
                    <Link to="/manage_v2_secure_auth/announcements" style={{ position: 'relative', color: 'var(--text-color)', fontWeight: '500', textDecoration: 'none', fontSize: '0.9em', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className="fas fa-bullhorn" />Announcements
                        {hasUnread && (
                            <span style={{ position: 'absolute', top: '-3px', right: '-7px', width: '9px', height: '9px', backgroundColor: 'var(--red-accent)', borderRadius: '50%', border: '2px solid var(--card-bg-color)' }} />
                        )}
                    </Link>

                    {/* Subscription timer */}
                    {sub?.timer_text && <SubscriptionBadge timerClass={sub.timer_class} timerText={sub.timer_text} />}

                    <a href="https://wa.me/917976996433?text=Hi%20Search%20is%20End!,%20I%20need%20help%20with%20my%20Admin%20Panel" target="_blank" rel="noopener noreferrer" style={{ backgroundColor: '#25D366', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '50px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9em', minHeight: '38px', whiteSpace: 'nowrap', textDecoration: 'none' }} title="Help & Support">
                        <i className="fab fa-whatsapp" style={{ fontSize: '1.1em' }} />
                        <span className="hide-on-mobile">Support</span>
                    </a>

                    <button onClick={handleLogout} style={{ backgroundColor: 'var(--red-accent)', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '50px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9em', minHeight: '38px', whiteSpace: 'nowrap' }}>
                        <i className="fas fa-sign-out-alt" />Logout
                    </button>
                </div>
            </header>
            {/* ── Content ── */}
            <div style={{ maxWidth: '1100px', margin: (location.pathname.endsWith('/pos') ? '10px auto' : '0 auto'), padding: 'clamp(20px, 4vw, 40px) clamp(12px, 3vw, 30px)', minHeight: 'calc(100vh - 80px)' }}>
                <Joyride 
                    key={`joyride_${tourKey}`}
                    steps={joyrideSteps} 
                    run={runTour} 
                    continuous 
                    showSkipButton 
                    showProgress 
                    callback={handleJoyrideCallback} 
                    tooltipComponent={CustomTooltip}
                    styles={{
                        options: { primaryColor: 'var(--primary-orange)', zIndex: 10000 }
                    }}
                />
                {upgradeModal && <UpgradeModal feature={upgradeModal} onClose={() => setUpgradeModal(null)} />}
                
                {showScannerPrompt && (
                    <ScannerPromptModal 
                        onClose={() => setShowScannerPrompt(false)} 
                        token={token}
                        dashData={dashData}
                        user={user}
                        onScanSuccess={() => {
                            setShowScannerPrompt(false);
                            fetchDash(token); // Refresh dash data (total_items)
                            navigate('/manage_v2_secure_auth/manage-menu'); // Show the results!
                        }} 
                    />
                )}

                {sub?.status === 'Expired' && (
                    <div style={{
                        background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                        border: '1px solid #fca5a5',
                        color: '#991b1b',
                        padding: '16px 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '12px',
                        boxShadow: '0 4px 12px rgba(239,68,68,0.1)',
                        marginBottom: '20px',
                        borderRadius: '12px',
                        fontFamily: "'Outfit', sans-serif"
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'left' }}>
                            <i className="fas fa-exclamation-triangle" style={{ fontSize: '1.2rem', color: '#dc2626' }} />
                            <div>
                                <strong style={{ fontSize: '1.05rem' }}>Your {dashData?.settings?.trial_days || 7}-day free trial / subscription has expired!</strong>
                                <p style={{ margin: '4px 0 0 0', fontSize: '0.9em', color: '#7f1d1d' }}>
                                    To keep your digital QR menu active and regain full access, please upgrade to our {dashData?.settings?.lowest_plan_name || 'Basic'} Plan for just ₹{dashData?.settings?.lowest_plan_price || 11}/month or choose a Pro Plan.
                                </p>
                            </div>
                        </div>
                        <Link to="/manage_v2_secure_auth/subscription" style={{
                            background: '#dc2626',
                            color: '#fff',
                            padding: '8px 18px',
                            borderRadius: '50px',
                            fontWeight: '600',
                            textDecoration: 'none',
                            fontSize: '0.9em',
                            boxShadow: '0 4px 10px rgba(220,38,38,0.2)',
                            transition: 'transform 0.2s'
                        }} onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'} onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}>
                            Upgrade Now
                        </Link>
                    </div>
                )}

                <Routes>
                    <Route path="/"                    element={<DashboardOverview user={user} dashData={dashData} token={token} setDash={setDash} onRestartTour={handleRestartTour} onUpgradeClick={setUpgradeModal} />} />
                    <Route path="/bulk-entry"           element={<BulkEntryWrapped token={token} />} />
                    <Route path="/manage-categories"    element={<ManageCategories token={token} />} />
                    <Route path="/manage-menu"          element={<ManageMenu token={token} />} />
                    <Route path="/settings"             element={<RestaurantSettings token={token} onLogoUpdate={() => fetchDash(token)} />} />
                    <Route path="/manage-coupons"       element={<ManageCoupons token={token} features={dashData?.features} />} />
                    <Route path="/manage-offers"        element={<ManageOffers token={token} features={dashData?.features} />} />
                    <Route path="/manage-notifications" element={<ManageNotifications token={token} features={dashData?.features} />} />
                    <Route path="/how-to-use"           element={<HowToUse features={dashData?.features} />} />
                    <Route path="/qr-templates"         element={<ManageQR token={token} />} />
                    <Route path="/analytics"            element={<Analytics token={token} />} />
                    <Route path="/pos"                  element={<POS token={token} isFreeTier={dashData?.settings?.is_free_tier} />} />
                    <Route path="/kitchen"              element={<KitchenView token={token} isAdmin={true} />} />
                    <Route path="/service-requests"     element={<ManageServiceRequests token={token} user={user} />} />
                    <Route path="/staff"                element={<StaffManagement token={token} />} />
                    <Route path="/billing-settings"     element={<BillingSettings token={token} />} />
                    <Route path="/subscription"         element={<ManageSubscription token={token} />} />
                    <Route path="/verify_payment"       element={<VerifyPayment />} />
                    <Route path="/announcements"        element={<Announcements token={token} dashData={dashData} onRead={() => fetchDash(token)} />} />
                    <Route path="/gallery"              element={<Gallery token={token} />} />
                    <Route path="/loyalty"              element={<LoyaltyProgram token={token} features={dashData?.features} />} />
                    <Route path="/monetization"         element={<AdminMonetization />} />
                </Routes>
            </div>
            {!location.pathname.endsWith('/pos') && <Footer />}
        </div>
    );
};

export default AdminDashboard;

